// SPDX-License-Identifier: FSL-1.1-Apache-2.0
/**
 * @module StatsCard
 * @package @geenius/analytics/react-css
 * @description Renders the React CSS summary-stat card used across analytics
 * page headers and dashboard compositions. The component combines an optional
 * icon, headline value, and trend indicator into a compact card surface.
 */

import { Badge, Card, CardContent } from "@geenius/ui/react-css";
import type React from "react";
import { type AnalyticsErrorStateValue, ErrorState } from "./ErrorState";

const SPARKLINE_WIDTH = 120;
const SPARKLINE_HEIGHT = 36;

function getSparklinePoints(values: number[]): string | null {
  const finiteValues = values.filter((value) => Number.isFinite(value));
  if (finiteValues.length < 2) return null;

  const min = Math.min(...finiteValues);
  const max = Math.max(...finiteValues);
  const range = max - min || 1;
  const lastIndex = finiteValues.length - 1;

  return finiteValues
    .map((value, index) => {
      const x = (index / lastIndex) * SPARKLINE_WIDTH;
      const y = SPARKLINE_HEIGHT - ((value - min) / range) * SPARKLINE_HEIGHT;
      return `${x.toFixed(2)},${y.toFixed(2)}`;
    })
    .join(" ");
}

/**
 * Props for rendering the React CSS stats card.
 *
 * @property label Human-readable label for the metric being displayed.
 * @property value Formatted or raw metric value to display prominently.
 * @property icon Optional icon shown beside the metric content.
 * @property change Optional change string rendered below the main value.
 * @property trend Optional direction token used for the change indicator.
 * @property sparkline Optional numeric series rendered as a compact trend line.
 * @property sparklineLabel Accessible label for the sparkline image.
 * @property className Optional class name merged into the card wrapper.
 */
export interface StatsCardProps {
  label: string;
  value: string | number;
  icon?: string;
  change?: string;
  trend?: "up" | "down" | "neutral";
  sparkline?: number[];
  sparklineLabel?: string;
  error?: AnalyticsErrorStateValue;
  onRetry?: () => void;
  className?: string;
}

/**
 * Renders the React CSS summary-stat card.
 *
 * @param props Metric label, value, and optional trend presentation props.
 * @returns A card element showing the supplied summary metric.
 */
export function StatsCard({
  label,
  value,
  icon,
  change,
  error,
  onRetry,
  trend,
  sparkline,
  sparklineLabel,
  className,
}: StatsCardProps): React.ReactElement {
  const trendLabel =
    trend === "up" ? "increased" : trend === "down" ? "decreased" : "changed";
  const sparklinePoints = getSparklinePoints(sparkline ?? []);

  if (error) {
    return (
      <Card className={className}>
        <CardContent className="gn-analytics-stats-card">
          <ErrorState error={error} title={label} onRetry={onRetry} />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={className}>
      <CardContent className="gn-analytics-stats-card">
        {icon && (
          <div
            className="gn-analytics-stats-card__icon"
            data-analytics-stat-icon
            aria-hidden="true"
          >
            {icon}
          </div>
        )}
        <div
          className="gn-analytics-stats-card__content"
          data-analytics-stat-content
        >
          <span
            className="gn-analytics-stats-card__label"
            data-analytics-stat-label
          >
            {label}
          </span>
          <span
            className="gn-analytics-stats-card__value"
            data-analytics-stat-value
          >
            {value}
          </span>
          {change && (
            <Badge
              size="sm"
              variant={
                trend === "up"
                  ? "success"
                  : trend === "down"
                    ? "danger"
                    : "secondary"
              }
            >
              <span
                className="gn-analytics-stats-card__change"
                data-analytics-stat-change
                data-trend={trend || "neutral"}
                role="img"
                aria-label={`${trendLabel} ${change}`}
              >
                <span aria-hidden="true">
                  {trend === "up" ? "↑" : trend === "down" ? "↓" : ""}
                </span>{" "}
                {change}
              </span>
            </Badge>
          )}
          {sparklinePoints ? (
            <svg
              className="gn-analytics-stats-card__sparkline"
              data-analytics-stat-sparkline
              viewBox={`0 0 ${SPARKLINE_WIDTH} ${SPARKLINE_HEIGHT}`}
              role={sparklineLabel ? "img" : undefined}
              aria-label={sparklineLabel}
              aria-hidden={sparklineLabel ? undefined : true}
              focusable="false"
            >
              <polyline
                className="gn-analytics-stats-card__sparkline-line"
                points={sparklinePoints}
                vectorEffect="non-scaling-stroke"
              />
            </svg>
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
}
